import { http, setAuthToken, clearAuthToken, getAuthToken } from './client'
import { API_BASE } from '../platform'
import { isDemoEmail } from '../demo'
import type { LoginResult, PublicUser, ReservationListResult } from './types'

/**
 * Login AND registration in one call: POST /security/login { username, password }.
 * An unknown username auto-registers (result.isNew === true). Must be sent WITHOUT
 * the mobile-token header (noAuth) or the firewall short-circuits the response.
 * Returns a JWT in result.token which we persist and replay as the mobile-token header.
 */
export async function login(username: string, password: string): Promise<LoginResult> {
  const res = await http.post<LoginResult>('/security/login', { username, password }, { noAuth: true })
  if (res?.loginResult && res.token) setAuthToken(res.token)
  return res
}

/** True when the email already has an (enabled) account — used to label login vs. register. */
export async function checkUsername(username: string): Promise<boolean> {
  if (isDemoEmail(username)) return true // demo accounts always "exist" → show the login step
  try {
    return await http.post<boolean>('/security/checkUsername', { username }, { noAuth: true })
  } catch {
    return false
  }
}

/** Sends the password-reset email. Always resolves true (no account enumeration). */
export async function rememberPassword(username: string): Promise<boolean> {
  return http.post<boolean>('/security/rememberPassword', { username }, { noAuth: true })
}

/** Current member from the stored token (null if not authenticated / token invalid). */
export async function getCurrentUser(): Promise<PublicUser | null> {
  if (!getAuthToken()) return null
  try {
    return await http.get<PublicUser | null>('/security/user')
  } catch {
    return null
  }
}

/** Update the member profile (requires auth). */
export async function updateProfile(patch: Partial<PublicUser>): Promise<PublicUser | null> {
  return http.post<PublicUser | null>('/security/updateProfile', patch)
}

/** Clear the token and best-effort kill the server session/remember-me cookie. */
export async function logout(): Promise<void> {
  clearAuthToken()
  try {
    await fetch(`${API_BASE}/security/logout`, { credentials: 'include' })
  } catch {
    /* ignore */
  }
}

/** The member's bookings, split active (departure in the future) vs archived. */
export async function getReservationList(): Promise<ReservationListResult> {
  const data = await http.get<ReservationListResult | unknown[]>('/api/reservationList/')
  // backend returns an empty array (not an object) when unauthenticated
  if (Array.isArray(data)) return { activeList: [], archivedList: [] }
  return data as ReservationListResult
}
